import React from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Typography,
} from '@mui/material';

const ACTION_LABELS = {
    upgrade: 'Incorrect → Correct',
    protected_downgrade: 'Protected override',
    blocked_downgrade: 'Blocked downgrade',
    review_required: 'Review required',
    ungradable: 'Not auto-gradable',
    unchanged: 'Unchanged',
};

const ACTION_COLORS = {
    upgrade: 'success',
    protected_downgrade: 'warning',
    blocked_downgrade: 'warning',
    review_required: 'info',
    ungradable: 'default',
    unchanged: 'default',
};

export const getApplicableUpgradeCount = (preview) => (
    preview?.items?.filter((item) => item.action === 'upgrade' && item.apply_allowed).length || 0
);

export const canManageHomeworkGrades = (userRole, isStudentView) => (
    userRole === 'admin' && !isStudentView
);

const BooleanGrade = ({ value }) => {
    if (value === true) return 'Correct';
    if (value === false) return 'Incorrect';
    return 'Pending';
};

const RegradePreviewDialog = ({ open, preview, applying, onClose, onApply }) => {
    const items = preview?.items || [];
    const counts = preview?.counts || {};
    const upgradeCount = getApplicableUpgradeCount(preview);
    const blockedCount = (counts.protected_downgrade || 0) + (counts.blocked_downgrade || 0);

    return (
        <Dialog open={open} onClose={applying ? undefined : onClose} fullWidth maxWidth="md">
            <DialogTitle>Review regrade preview</DialogTitle>
            <DialogContent>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Students will continue to see only Correct or Incorrect. This action can apply reviewed
                    Incorrect → Correct upgrades only; it will never downgrade a stored correct grade.
                </Alert>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                    <Chip color="success" label={`${upgradeCount} upgrade${upgradeCount === 1 ? '' : 's'}`} />
                    <Chip color="warning" label={`${blockedCount} blocked downgrade${blockedCount === 1 ? '' : 's'}`} />
                    <Chip label={`${counts.unchanged || 0} unchanged`} />
                    {(counts.review_required || 0) > 0 && (
                        <Chip color="info" label={`${counts.review_required} need review`} />
                    )}
                    {(counts.ungradable || 0) > 0 && (
                        <Chip label={`${counts.ungradable} not auto-gradable`} />
                    )}
                </Stack>

                <Divider sx={{ mb: 1 }} />
                <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
                    {items.map((item) => (
                        <Box
                            key={item.hw_answer_id}
                            sx={{
                                py: 1.25,
                                px: 1,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 2,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <Box>
                                <Typography variant="body2" fontWeight="bold">
                                    Question {item.hw_question_id} · Answer {item.hw_answer_id}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    <BooleanGrade value={item.current_is_correct} /> →{' '}
                                    <BooleanGrade value={item.proposed_is_correct} />
                                </Typography>
                            </Box>
                            <Chip
                                size="small"
                                color={ACTION_COLORS[item.action] || 'default'}
                                label={ACTION_LABELS[item.action] || item.action}
                            />
                        </Box>
                    ))}
                    {items.length === 0 && (
                        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                            No answers were included in this preview.
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={applying}>Cancel</Button>
                <Button
                    variant="contained"
                    color="success"
                    onClick={onApply}
                    disabled={applying || upgradeCount === 0}
                >
                    {applying ? 'Applying…' : `Apply ${upgradeCount} upgrade${upgradeCount === 1 ? '' : 's'}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RegradePreviewDialog;
